import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, useTheme, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  BookOpen, 
  Sun, 
  Moon, 
  LogOut, 
  ArrowLeft,
  Search,
  X,
  ChevronRight,
  Circle,
  Triangle,
  Square,
  Loader2
} from "lucide-react";
import GeometricCover from "@/components/GeometricCover";

// Reading level configuration
const LEVELS = {
  "all": {
    name: "All Levels",
    icon: BookOpen,
    className: "bg-slate-50 dark:bg-slate-950/30 text-slate-700 dark:text-slate-300",
    description: "Browse all stories"
  },
  "1": {
    name: "Level 1 - Easy",
    icon: Circle,
    className: "bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300",
    description: "Perfect for early readers"
  },
  "2": {
    name: "Level 2 - Medium",
    icon: Triangle,
    className: "bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-300",
    description: "For growing readers"
  },
  "3": {
    name: "Level 3 - Challenge",
    icon: Square,
    className: "bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300",
    description: "For strong readers"
  }
};

export default function Library() {
  const { levelId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedLevel, setSelectedLevel] = useState(levelId || "1");
  const [error, setError] = useState(null);

  const level = LEVELS[selectedLevel] || LEVELS["1"];

  const fetchStories = useCallback(async (levelFilter = null, searchTerm = "") => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      // Only add level filter if not "all"
      if (levelFilter && levelFilter !== "all") {
        params.append('level', levelFilter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const token = localStorage.getItem('kbn_session_token');
      const response = await fetch(`${API}/local-stories?${params}`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) throw new Error('Failed to fetch stories');

      const data = await response.json();
      
      // Sort stories by level, then by title
      const sortedStories = (data.stories || []).sort((a, b) => {
        const levelA = parseInt(a.level) || 1;
        const levelB = parseInt(b.level) || 1;
        if (levelA !== levelB) return levelA - levelB;
        return a.title.localeCompare(b.title);
      });
      
      setStories(sortedStories);
    } catch (err) {
      console.error('Error fetching stories:', err);
      setError('Unable to load stories. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStories(selectedLevel, search);
  }, [fetchStories, selectedLevel, search]);

  // Update URL when level changes
  useEffect(() => {
    if (levelId !== selectedLevel) {
      navigate(`/library/${selectedLevel}`, { replace: true });
    }
  }, [selectedLevel, levelId, navigate]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
  };

  const handleStoryClick = (story) => {
    navigate(`/read/${story.id}`, { state: { level: selectedLevel, story } });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/home')}
                className="rounded-full"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-primary" strokeWidth={2.5} />
                </div>
                <span className="font-heading font-bold hidden sm:inline">Kitt's Book Nook</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-library"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full p-1">
                    <Avatar className="w-9 h-9">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-primary/10 text-primary font-bold">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Level Header */}
        <div className="mb-6 animate-slide-up">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-3 ${level.className}`}>
            <level.icon className="w-4 h-4" />
            {level.name}
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-black">
            Find your next story
          </h1>
          <p className="text-muted-foreground mt-1">{level.description}</p>
        </div>

        {/* Level Tabs */}
        <div className="flex gap-2 mb-6 animate-slide-up" style={{ animationDelay: '0.05s' }}>
          {Object.entries(LEVELS).map(([id, lvl]) => (
            <Button
              key={id}
              variant={selectedLevel === id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedLevel(id)}
              className={`rounded-full ${selectedLevel === id ? '' : lvl.className}`}
              data-testid={`level-tab-${id}`}
            >
              <lvl.icon className="w-3 h-3 mr-1" />
              {id === 'all' ? 'All' : `Level ${id}`}
            </Button>
          ))}
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by title or author..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-12 pr-12 h-12 rounded-full border-2 focus:ring-2 focus:ring-primary"
              data-testid="search-input"
            />
            {searchInput && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-full"
                data-testid="clear-search"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
          {search && (
            <p className="mt-2 text-sm text-muted-foreground">
              Showing results for "{search}"
            </p>
          )}
        </form>

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => fetchStories(selectedLevel, search)} data-testid="retry-button">
              Try Again
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[2/3] rounded-xl" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Stories Grid */}
        {!loading && !error && (
          <>
            {stories.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {search ? `No stories found for "${search}"` : 'No stories found at this level'}
                </p>
                {search && (
                  <Button variant="ghost" onClick={clearSearch} className="mt-4">
                    Clear search
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
                {stories.map((story, index) => (
                  <button
                    key={story.id}
                    onClick={() => handleStoryClick(story)}
                    className="text-left group animate-slide-up"
                    style={{ animationDelay: `${Math.min(index * 0.05, 0.5)}s` }}
                    data-testid={`story-card-${story.id}`}
                  >
                    <div className="aspect-[2/3] relative rounded-xl overflow-hidden bg-muted mb-3 shadow-card group-hover:shadow-lg group-hover:scale-[1.03] transition-all duration-300">
                      {/* Geometric Cover SVG - Data-driven by level */}
                      <GeometricCover 
                        title={story.title}
                        author={story.author}
                        level={story.level}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                        <span className="text-white text-sm font-medium flex items-center gap-1">
                          Start Reading <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                    <h3 className="font-heading font-bold text-sm sm:text-base line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                      {story.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground line-clamp-1">
                      {story.author}
                    </p>
                    {story.total_words > 0 && (
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {Math.round(story.total_words / 200)} min read
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
